import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
    interpolate,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CircularPager } from '@/feature/circular-pager/CircularPager';
import { getRealIndex, moddedIndex, moveToClosestIndex } from '@/feature/circular-pager/circular-helpers';
import { usePagerSmoothCloseOnRemoval } from '@/feature/circular-pager/hooks/use-pager-smooth-close-on-removal';
import { CircularPagerImperativeApi, CircularPagerPageProps } from '@/feature/circular-pager/types';
import { useResponsiveDimensions } from '@/feature/responsive-design/hooks/use-responsive-dimensions';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { isPGA } from '@/utils/league';
import { sortEvents } from '@/utils/sortEvents';

import { Handle } from '../alerts/components/Modal';
import { AnalyticsEvent } from '../analytics/constants';
import { useLiveEventsPropsUpdate } from '../betslip-pickem/hooks/use-live-props-update';
import { SHEET_BORDER_RADIUS } from '../circular-pager/CircularPageSlot';
import {
    FullScreenPagerTransitionProvider,
    useFullScreenPagerTransition,
} from '../circular-pager/FullScreenPagerTransitionProvider';
import { ScrollableModalContextProvider } from '../circular-pager/ScrollableModalContextProvider';
import { SheetBackground } from '../circular-pager/SheetBackground';
import { useGQLRequestPolicy } from '../guest-user/hooks/use-gql-request-policy';
import { PlayerFiltersProvider } from '../lobby/hooks/use-player-filters';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { EventDetailsScreen } from './EventDetailsScreen';
import { useSinglePlayerProfile } from './SinglePlayerProfileProvider';
import { EventDetailsPickBar } from './components/EventDetailsPickbar';
import { EmptyModal, EventDetailsSheetScreen, createEventDetailsScreenKey } from './components/EventDetailsSheetScreen';
import { EventsCarouselTabBar, getEventCarouselHeight } from './components/EventsCarouselTabBar';
import { SinglePlayerProfileModal } from './components/SinglePlayerProfileModal';

export type FantasyEventsSheetsParamsList = NativeStackScreenProps<RootStackParamList, 'FantasyEventsSheets'>;

export const FantasyEventsSheetsScreen = () => {
    const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();
    usePagerSmoothCloseOnRemoval();

    const selectedTab = useEventsTabsStore(state => state.selectedTab);

    return (
        <FullScreenPagerTransitionProvider key={params.contextKey} initialIndex={selectedTab}>
            <FantasyEventsSheetsTabs />
        </FullScreenPagerTransitionProvider>
    );
};

const PAGE_GAP = 20;

const FantasyEventsSheetsTabs = () => {
    const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();

    const { width, height } = useResponsiveDimensions();
    const requestPolicy = useGQLRequestPolicy();

    const [{ data: upcomingEvents, fetching, stale }] = useLeagueUpcomingEventsQuery({
        variables: { league: params.league },
        requestPolicy,
    });

    useLiveEventsPropsUpdate({
        events: upcomingEvents?.getUpcomingEventsV2,
        options: {
            subscribeToGameClockUpdates: true,
            subscribeToMarketUpdates: true,
            debugTag: 'FantasyEventsSheetsTabs',
        },
    });

    const sortedEvents = useMemo(() => {
        const feed = upcomingEvents?.getUpcomingEventsV2 ?? [];
        return feed.toSorted((a, b) => sortEvents(a, b));
    }, [upcomingEvents?.getUpcomingEventsV2]);

    const sortedEventIds = useMemo(() => {
        return sortedEvents.map(ev => ev.id);
    }, [sortedEvents]);

    const totalPages = sortedEventIds?.length || 0;

    const noData = !fetching && !stale && sortedEvents.length === 0;

    const setSelectedTab = useEventsTabsStore(state => state.actions.setSelectedTab);
    const selectedTab = useEventsTabsStore(state => state.selectedTab);

    const internalTab = useRef(selectedTab);

    const validInitialTab = totalPages === 0 ? 0 : Math.min(Math.max(selectedTab, 0), totalPages - 1);

    const { selectedTabSharedValue, pageAnim, animatedIndex } = useFullScreenPagerTransition();

    useEffect(() => {
        if (params.eventId && sortedEventIds.length > 1) {
            const found = sortedEventIds.findIndex(id => id === params.eventId);
            if (found !== -1) {
                setSelectedTab(found);
            }
        }
    }, [params.eventId, params?.league, setSelectedTab, sortedEventIds]);

    const pagerRef = useRef<CircularPagerImperativeApi>(null);

    // if the store's selectedTab changes from outside, jump the pager
    useEffect(() => {
        if (internalTab.current !== selectedTab && totalPages > 0) {
            internalTab.current = selectedTab;
            selectedTabSharedValue.value = selectedTab;
            pagerRef.current?.setPage(moveToClosestIndex(pageAnim.value, selectedTab, totalPages), { animated: true });
        }
    }, [selectedTab, totalPages, selectedTabSharedValue, pageAnim]);

    const handlePageChange = useCallback(
        (rawIndex: number) => {
            const realIndex = totalPages === 0 ? rawIndex : moddedIndex(rawIndex, totalPages);
            runOnJS(() => {
                selectedTabSharedValue.value = realIndex;
                internalTab.current = realIndex;
                setSelectedTab(realIndex);
            })();
        },
        [setSelectedTab, selectedTabSharedValue, totalPages]
    );
    const insets = useSafeAreaInsets();

    const onlyOnePage = totalPages === 1 || params.eventId === '';

    const headerHeight = onlyOnePage ? 0 : getEventCarouselHeight(params.league);

    const { playerProfileAnimatedIndex, isPlayerProfileModalOpen } = useSinglePlayerProfile();

    const renderWebPage = useCallback(() => {
        const eventInfo = sortedEvents[selectedTab];
        if (!eventInfo) {
            return null;
        }

        return (
            <ScrollableModalContextProvider>
                {/** key=event.id so we unmount everything when event changes for web, as for web we have a single page */}
                <PlayerFiltersProvider key={createEventDetailsScreenKey(eventInfo)}>
                    <EventDetailsScreen
                        isActive={true}
                        eventInfo={eventInfo}
                        eventDedicatedHeaderInfo={isPGA(params.league) ? sortedEvents[selectedTab] : null}
                    />
                </PlayerFiltersProvider>
            </ScrollableModalContextProvider>
        );
    }, [selectedTab, params.league, sortedEvents]);

    const renderPage = useCallback(
        ({ offset, shouldFreeze, isActive, currentIndex }: CircularPagerPageProps) => {
            const realIndex = getRealIndex(offset, currentIndex, sortedEventIds.length);
            const eventInfo = sortedEvents[realIndex];

            if (!eventInfo) {
                // If the modal is opened before any events are loaded, display an empty modal.
                // This can occur, for example, when the user launches it from a player profile sheet from lobby
                // or selects a league that has only one event.
                return <EmptyModal />;
            }

            return (
                <EventDetailsSheetScreen
                    shouldFreeze={shouldFreeze}
                    index={realIndex}
                    eventInfo={eventInfo}
                    isActive={isActive}
                    offset={offset}
                    eventDedicatedHeaderInfo={isPGA(params.league) ? sortedEvents[offset] : null}
                />
            );
        },
        [sortedEventIds.length, params.league, sortedEvents]
    );

    const positionStyle = isWeb ? undefined : common.absolute;

    const topContentHeight = useMemo(() => {
        return getEventCarouselHeight(params.league);
    }, [params.league]);

    const isEventSheetFullyOpen = useSharedValue(false);

    // Track changes to animatedIndex outside of the style (using animatedIndex.value directly in the style would cause a flicker when opening event modal)
    useAnimatedReaction(
        () => animatedIndex.value,
        currentValue => {
            isEventSheetFullyOpen.value = currentValue === 1;
        }
    );

    // This creates a smooth effect of the event sheet shrinking when the player's profile modal is opened.
    const scaleOnlyStyle = useAnimatedStyle(() => {
        const transformOrigin = isPlayerProfileModalOpen.value ? ['50%', headerHeight, 0] : ['50%', '50%', 0];

        return {
            transformOrigin,
            transform: [
                {
                    scale: interpolate(
                        playerProfileAnimatedIndex.value,
                        [-1, 0, 1],
                        [1, (width - 16) / width, (width - 16) / width]
                    ),
                },
            ],
        };
    }, [width, headerHeight]);

    // Shrink + When scrolling Player Profile to the top of the page, the Event Detail sheet also scrolls up and fixes to the top
    const scaleAndTranslateStyle = useAnimatedStyle(() => {
        const transformOrigin = isPlayerProfileModalOpen.value ? ['50%', headerHeight, 0] : ['50%', '50%', 0];
        const translateY = interpolate(playerProfileAnimatedIndex.value, [-1, 0, 1], [0, 0, -topContentHeight]);

        return {
            transformOrigin,
            transform: [
                {
                    scale: interpolate(
                        playerProfileAnimatedIndex.value,
                        [-1, 0, 1],
                        [1, (width - 16) / width, (width - 16) / width]
                    ),
                },
                // Only include translateY if the event sheet and player profile sheet are not fully open
                ...(playerProfileAnimatedIndex.value === 1 && isEventSheetFullyOpen.value ? [] : [{ translateY }]),
            ],
        };
    }, [topContentHeight, width, headerHeight]);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList, 'FantasyEventsSheets'>>();

    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const trackPageSwipe = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PAGER_EVENT_SWIPE);
    }, []);

    return (
        <Box width={width} height={'100%'}>
            <SheetBackground />
            {onlyOnePage ? null : (
                <MaxWidthWrapper>
                    <EventsCarouselTabBar />
                </MaxWidthWrapper>
            )}

            <Animated.View
                style={[
                    {
                        height,
                        top: insets.top,
                        width,
                        backgroundColor: isWeb ? designSystem.colors.neutral900 : undefined,
                    },
                    positionStyle,
                    onlyOnePage ? scaleOnlyStyle : scaleAndTranslateStyle,
                ]}
            >
                <CircularPager
                    ref={pagerRef}
                    pageCallbackNode={pageAnim}
                    isEmpty={noData}
                    renderEmptyView={NoEvents}
                    initialIndex={validInitialTab}
                    onPageChange={handlePageChange}
                    pageGap={PAGE_GAP}
                    renderPage={isWeb ? renderWebPage : renderPage}
                    aboveSheetHeaderHeight={headerHeight}
                    onClose={onClose}
                    onPageSwipe={trackPageSwipe}
                    onlyOnePage={onlyOnePage}
                />
                <Box
                    style={{
                        transform: [{ translateY: height }],
                        width,
                        height,
                    }}
                    zIndex={-1}
                    backgroundColor={ds.colorToken.background.neutral.base}
                />
            </Animated.View>
            <EventDetailsPickBar />
            {!isWeb ? <SinglePlayerProfileModal onlyOnePage={onlyOnePage} circularPagerRef={pagerRef} /> : null}
        </Box>
    );
};

const NoEvents = () => {
    return (
        <Box
            paddingHorizontal={'s24'}
            flex={1}
            backgroundColor={ds.colorToken.background.neutral.base}
            style={styles.noEventsContainer}
        >
            <Handle handleColor={designSystem.colors.neutral500} handleContainerStyle={styles.handle} />
            <Text variant={'bodyMedium'} fontWeight={'bold'} marginTop={'s24'}>
                No events available.
            </Text>
        </Box>
    );
};

const styles = StyleSheet.create({
    noEventsContainer: {
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    handle: {
        backgroundColor: 'transparent',
    },
});
